import React, { useState, useEffect, useMemo } from 'react';
import { Grid, Paper, Button, Container, Typography, CircularProgress, Box } from '@mui/material';
import { styled } from 'styled-components'; // ou @mui/material/styles
import ReactApexChart from 'react-apexcharts';

// Componentes que você já tem
import PopoverFiltroOs from '../../PopoverFiltroOs'; // Ajuste o caminho
import CalendarSection from '../components/dashboard/OsCalendarSection';
import VerticalBarChart from '../components/dashboard/OsVerticalBarChart';

// Novos componentes para o Modal
import AgendaModal from '../components/dashboard/AgendaModal'; // Criaremos abaixo

// Seu serviço de API (ou axios)
import { getOssDashboard, getAgendamentosDia } from '../../api';
import { cores, coresGrafico } from '../../../styles/styleUtils';

// Wrapper para os gráficos
const ChartWrapper = styled(Paper)`
  padding: 24px;
  height: 100%;
`;

// Define os status esperados para os gráficos de barra e pizza
const STATUS_LABELS = ['Pendente', 'Aberta', 'Finalizada', 'Cancelada', 'Reprovada', 'Atrasada'];

const dataAtual = new Date();
const primeiroDiaDoMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth(), 1).toISOString().split('T')[0];
const ultimoDiaDoMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth() + 1, 0).toISOString().split('T')[0];
  
// Estado inicial dos filtros
const initialStateFiltro = {
  dataInicio: primeiroDiaDoMes,
  dataFim: ultimoDiaDoMes,
  unidades: [],
  classificacoes: [],
  responsaveis: [],
  fornecedores: [],
  distribuidoras: [], // Mesmo que não usado na query, está no seu popover
};

function HomeOs() {
  const [filtro, setFiltro] = useState(initialStateFiltro);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  // Estado do Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [agendamentosDoDia, setAgendamentosDoDia] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);

  // Carrega os dados do dashboard
  const fetchData = async (currentFilters) => {
    setLoading(true);
    try {
      const response = await getOssDashboard(currentFilters);
      setDashboardData(response);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      // Tratar erro (ex: snackbar)
    }
    setLoading(false);
  };

  // Busca inicial e ao mudar filtros
  useEffect(() => {
    fetchData(filtro);
  }, [filtro]);

  // --- Lógica dos Filtros ---
  const handleOpenFiltro = (event) => setPopoverAnchor(event.currentTarget);
  const handleCloseFiltro = () => setPopoverAnchor(null);

  const handleConfirmFiltro = (novosFiltros) => {
    setFiltro(novosFiltros);
    handleCloseFiltro();
  };

  const handleLimparFiltro = () => {
    setFiltro(initialStateFiltro);
    handleCloseFiltro();
  };

  // --- Lógica do Calendário ---
  const handleDayClick = async (date) => {
    // A data vem do componente CalendarSection
    setSelectedDate(date);
    setModalOpen(true);
    setLoadingModal(true);

    try {
      // Formata a data para YYYY-MM-DD
      const dataFormatada = date.toISOString().split('T')[0];
      const response = await getAgendamentosDia({ data: dataFormatada, ...filtro });
      setAgendamentosDoDia(response);
    } catch (error) {
      console.error('Erro ao buscar agendamentos do dia:', error);
    }
    setLoadingModal(false);
  };

  // --- Preparação dos Dados para Gráficos (useMemo) ---

  // 1. Gráfico de Status (Barra)
  const statusChartData = useMemo(() => {
    if (!dashboardData?.statusCounts) return { series: [], options: {} };

    const counts = dashboardData.statusCounts.reduce((acc, item) => {
      acc[item.status] = item.count;
      return acc;
    }, {});

    const seriesData = STATUS_LABELS.map((status) => counts[status] || 0);

    return {
      series: [{ name: 'Ordens de Serviço', data: seriesData }],
      options: {
        chart: { type: 'bar' },
        plotOptions: { bar: { distributed: true } },
        colors: coresGrafico,
        legend: { show: false },
        xaxis: { categories: STATUS_LABELS },
        yaxis: { title: { text: 'Quantidade' } },
        tooltip: { y: { formatter: (val) => val } },
      },
    };
  }, [dashboardData]);

  // 2. Calendário
  const calendarDates = useMemo(() => {
    if (!dashboardData?.agendamentos) return [];
    // O componente CalendarSection espera {dia, mes, ano}
    return dashboardData.agendamentos.map((item) => {
      const [year, month, day] = item.data.split('-').map(Number);
      return { dia: day, mes: month, ano: year };
    });
  }, [dashboardData]);

  // 3. Top 5 Ocorrências (Barra Horizontal)
  const topOcorrenciasData = useMemo(() => {
    if (!dashboardData?.topOcorrencias) return { series: [], options: {} };

    const labels = dashboardData.topOcorrencias.map((item) => item.classificacaoInfo?.nome || item._id);
    const data = dashboardData.topOcorrencias.map((item) => item.count);

    return {
      series: [{ name: 'Ocorrências', data }],
      options: {
        chart: { type: 'bar' },
        plotOptions: { bar: { horizontal: true, distributed: true } },
        colors: coresGrafico,
        legend: { show: false },
        xaxis: { categories: labels, title: { text: 'Quantidade' } },
        tooltip: { x: { formatter: (val) => val } },
      },
    };
  }, [dashboardData]);

  // 4. Top 5 Garantias (Barra Horizontal)
  const topGarantiasData = useMemo(() => {
    if (!dashboardData?.topGarantias) return { series: [], options: {} };

    const labels = dashboardData.topGarantias.map((item) => item.inversorInfo?.modelo || item._id);
    const data = dashboardData.topGarantias.map((item) => item.count);

    return {
      series: [{ name: 'Garantias', data }],
      options: {
        chart: { type: 'bar' },
        plotOptions: { bar: { horizontal: true, distributed: true } },
        colors: coresGrafico,
        legend: { show: false },
        xaxis: { categories: labels, title: { text: 'Quantidade' } },
        tooltip: { x: { formatter: (val) => val } },
      },
    };
  }, [dashboardData]);

  // 5. Gráfico de Status (Pizza)
  const statusPieData = useMemo(() => {
    if (!dashboardData?.statusCounts) return { series: [], options: {} };

    const counts = dashboardData.statusCounts.reduce((acc, item) => {
      acc[item.status] = item.count;
      return acc;
    }, {});

    const seriesData = STATUS_LABELS.map((status) => counts[status] || 0);

    return {
      series: seriesData,
      options: {
        chart: { type: 'pie' },
        labels: STATUS_LABELS,
        colors: coresGrafico,
        legend: { position: 'bottom' },
      },
    };
  }, [dashboardData]);

  // --- Renderização ---
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Dashboard</Typography>
        <Button
          variant="contained"
          onClick={handleOpenFiltro}
          sx={{ backgroundColor: cores.laranja, '&:hover': { backgroundColor: cores.laranjaEscuro } }}
        >
          Filtros
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Gráfico Status (Barra) */}
        <Grid item xs={12} md={8}>
          <ChartWrapper>
            <VerticalBarChart
              chartId="status-bar"
              title="Status dos Chamados"
              options={statusChartData.options}
              series={statusChartData.series}
              height={350}
            />
          </ChartWrapper>
        </Grid>

        {/* Calendário */}
        <Grid item xs={12} md={4}>
          <ChartWrapper>
            <CalendarSection receivableDates={calendarDates} onDoubleClick={handleDayClick} />
          </ChartWrapper>
        </Grid>

        {/* Top 5 Ocorrências */}
        <Grid item xs={12} md={6}>
          <ChartWrapper>
            {/* Usando seu VerticalBarChart, mas com opções horizontais */}
            <VerticalBarChart
              chartId="top-ocorrencias"
              title="Top 5 - Maiores Ocorrências"
              options={topOcorrenciasData.options}
              series={topOcorrenciasData.series}
              height={350}
            />
          </ChartWrapper>
        </Grid>

        {/* Top 5 Garantias */}
        <Grid item xs={12} md={6}>
          <ChartWrapper>
            {/* Usando seu VerticalBarChart, mas com opções horizontais */}
            <VerticalBarChart
              chartId="top-garantias"
              title="Top 5 - Garantias"
              options={topGarantiasData.options}
              series={topGarantiasData.series}
              height={350}
            />
          </ChartWrapper>
        </Grid>

        {/* Gráfico Status (Pizza) */}
        <Grid item xs={12} md={12}>
          <ChartWrapper>
            <Typography variant="h6" align="center" gutterBottom>
              Percentual de Chamados
            </Typography>
            {/* Usando o ReactApexChart diretamente para a pizza */}
            <ReactApexChart options={statusPieData.options} series={statusPieData.series} type="pie" height={350} />
          </ChartWrapper>
        </Grid>
      </Grid>

      {/* Popover de Filtro */}
      <PopoverFiltroOs
        open={popoverAnchor}
        onClose={handleCloseFiltro}
        onConfirm={handleConfirmFiltro}
        onLimpar={handleLimparFiltro}
        filtro={filtro}
        // type="dashboard" // Você pode ajustar o 'type' se necessário
      />

      {/* Modal do Calendário */}
      <AgendaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        date={selectedDate}
        agendamentos={agendamentosDoDia}
        loading={loadingModal}
      />
    </Container>
  );
}

export default HomeOs;